import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Search,
  Rocket,
  Megaphone,
  CreditCard,
  UserCog,
  ArrowRight,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { FAQScrollAccordion } from '../components/ui/faq-scroll-accordion';

const helpCategories = [
  {
    title: 'Getting Started',
    description: 'Learn how to create an account, set up your profile, and navigate the platform.',
    icon: Rocket,
    color: 'bg-orange-50 text-orange-500',
    link: '#faq',
  },
  {
    title: 'Campaigns',
    description: 'Find, join, and manage campaigns — whether you are a brand or an influencer.',
    icon: Megaphone,
    color: 'bg-blue-50 text-blue-600',
    link: '#faq',
  },
  {
    title: 'Payments',
    description: 'Understand how compensation works, payment methods, and payout timelines.',
    icon: CreditCard,
    color: 'bg-green-50 text-green-600',
    link: '#faq',
  },
  {
    title: 'Account & Security',
    description: 'Manage your account settings, verification, and keep your profile secure.',
    icon: UserCog,
    color: 'bg-purple-50 text-purple-600',
    link: '#faq',
  },
];

const popularArticles = [
  { title: 'How to create an account', target: 0 },
  { title: 'Campaign workflow explained', target: 3 },
  { title: 'Payment methods & timelines', target: 7 },
  { title: 'Creating a campaign as a brand', target: 8 },
  { title: 'Analytics & reports', target: 10 },
  { title: 'Referral program', target: 11 },
];

const faqs = [
  {
    question: 'How do I create an account?',
    answer:
      'Click the "Sign Up" button in the top right corner. Choose whether you are joining as a brand or an influencer, fill in your details (email, password, and display name), and agree to the Terms and Conditions. After registering, you will receive a verification email — click the link inside to activate your account. Passwords must be at least 6 characters long.',
  },
  {
    question: 'How does email verification work?',
    answer:
      'After registering, a verification email is sent to your address. Click the link in the email to verify your account. The verification page automatically checks your status every 5 seconds and redirects you to your dashboard once verified. If you did not receive the email, you can request a resend after a 60-second cooldown. You can also log out and return later.',
  },
  {
    question: 'What social media platforms are supported?',
    answer:
      'You can link Instagram, TikTok, YouTube, Facebook, and X (Twitter) accounts to your profile. Follower counts and engagement rates are tracked for each platform. Brands can specify which platforms they require for a campaign, and our system automatically detects posted content and tracks clicks through unique tracking links generated per influencer-campaign pair.',
  },
  {
    question: 'How does the campaign workflow work end-to-end?',
    answer:
      'The full workflow is: (1) Brand creates a campaign with requirements and budget. (2) Influencer browses and applies with a note. (3) Brand reviews applications and approves influencers — a tracking link is auto-generated. (4) Influencer submits a content draft for pre-approval. (5) Brand approves or requests revisions. (6) Influencer posts content with required hashtags, mentions, and tracking link. (7) Influencer submits proof of posting (URL + screenshots). (8) Automated verification checks run, then brand reviews. (9) Brand approves — payment processing begins.',
  },
  {
    question: 'What types of campaigns are available?',
    answer:
      'Brands can create campaigns using content templates: Product Review Post, Unboxing Experience, Lifestyle Integration, Tutorial/How-To, and Before/After. Campaigns span categories including Fashion, Beauty, Lifestyle, Travel, Food, Fitness, Technology, and Gaming. Compensation types include Money Only, Product Only, or Product + Money. Brands can also target specific demographics, niches, follower counts, and platforms.',
  },
  {
    question: 'What is the content pre-approval process?',
    answer:
      'Before posting, influencers submit a content draft including the planned caption, hashtags, mentions, and content files (images/videos). The brand reviews this draft and either approves it for posting or requests revisions with feedback. This ensures content aligns with brand guidelines before it goes live. Once approved, the influencer can post and proceed to proof submission.',
  },
  {
    question: 'How does proof submission and verification work?',
    answer:
      'After posting content, the influencer submits proof: the post URL, screenshots, and a content file. Our system runs automated verification checks including post URL validity, required hashtag presence, brand mentions, tracking link clicks, and partnership disclosure (#ad). If verification fails, the influencer is asked to revise. If it passes, the brand reviews and approves or rejects the submission.',
  },
  {
    question: 'How is payment handled?',
    answer:
      'Payments are processed securely through our platform. Brands fund campaigns with 70% upfront (held in escrow) and 30% after completion. Once a brand approves content, they process the payment with OTP verification (sent to their email). An admin then reviews and completes the payment. Influencers receive payment after content approval, typically within 7-14 days. Supported methods include GCash, Bank Transfer, and PayPal.',
  },
  {
    question: 'How do I set up or manage my payment methods?',
    answer:
      'Influencers can manage payment methods from the Payments page. You can add multiple methods (GCash, Bank Transfer, PayPal), edit existing ones, delete them, and set a default method. Payment history shows the campaign name, brand, amount, status (Pending, Processing, or Completed), date, reference number, and estimated payment date. You can also view a detailed payment timeline for each transaction.',
  },
  {
    question: 'How do I create a campaign as a brand?',
    answer:
      'Navigate to Create Campaign and complete the 6-step wizard: (1) Basic Information — name, description, category, image. (2) Campaign Details — timeline, budget, compensation type and amount. (3) Influencer Requirements — minimum followers, number of influencers, preferred niches, required platforms. (4) Content Requirements — content types, guidelines, hashtags, mentions, and a template selector. (5) Store Selection — all stores, specific stores, or regional. (6) Review and submit with terms acceptance. Campaign images must be under 5MB.',
  },
  {
    question: 'How does the brand payment process work?',
    answer:
      'When a brand approves an influencer\'s content, the payment becomes eligible for processing. The brand navigates to the Payments page, clicks "Process Payment," and enters an OTP sent to their email (valid for 5 minutes). The payment is then created with status "Processing" and an admin reviews and marks it as "Completed." This two-step process ensures security and accountability for all transactions.',
  },
  {
    question: 'What analytics and reports are available?',
    answer:
      'Influencers can view Earnings Reports (monthly breakdown), Campaign Performance Reports, and Ratings & Reviews Reports — filterable by last month, 3 months, 6 months, or last year, with PDF and Excel export. Brands get an advanced analytics suite including total reach, average engagement, cost per reach, social media analytics, link tracking (clicks by platform), content detection (auto-discovered posts), and website analytics with page views.',
  },
  {
    question: 'How does the referral program work?',
    answer:
      'The referral program allows influencers to earn bonuses by inviting friends. Share your unique referral link — when a friend completes their first campaign, you earn a bonus. Tiers include Bronze (1-5 referrals: ₱500 each + badge), Silver (6-15 referrals: ₱500 each + 5% earnings bonus + priority access), and Gold (16+ referrals: ₱700 each + 10% bonus + premium campaigns + event invitations). Bonuses are processed 7 days after your friend\'s first campaign. There is no limit on referrals.',
  },
  {
    question: 'How do notifications work and how can I customize them?',
    answer:
      'You receive notifications for campaign status updates, payments, messages, and referrals. Filter by type (All, Unread, Campaigns, Payments) and use "Mark all as read" or "Clear all." In notification settings, you can toggle email, push, and SMS notifications globally, plus enable or disable specific categories like campaign updates, payments, messages, and referrals — giving you full control over what you receive.',
  },
  {
    question: 'How do I verify my identity and profile?',
    answer:
      'After creating your account, go to your profile settings to complete verification. Link your social media accounts (Instagram, TikTok, YouTube, Facebook, X) so the system can verify follower counts and calculate engagement rates. Fill in your bio, categories/niches, location, age range, and gender. A complete and verified profile increases your chances of being approved for campaigns and helps brands find the right match.',
  },
  {
    question: 'What if I have an issue with a campaign or payment?',
    answer:
      'If you encounter any issues with a campaign or payment, you can reach out to our support team via the Contact Us page. For campaign-specific concerns, use the in-app Messages feature to communicate directly with the other party. We are committed to resolving disputes fairly and promptly. You can also request account deletion from your profile settings at any time, with data handled per our Privacy Policy.',
  },
  {
    question: 'Is my data secure on the platform?',
    answer:
      'Yes. We use CSRF protection on all forms, OTP verification for payment processing, and input sanitization to prevent injection attacks. Your personal data is handled in accordance with our Privacy Policy (last updated August 1, 2025). We do not share your information except as described in the policy — with relevant parties for campaigns, service providers, or as required by law. The platform is governed by Philippine law. For privacy concerns, contact hola@engagedmanila.com.',
  },
];

const HelpCenter = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredFaqs = searchQuery
    ? faqs.filter(
        (faq) =>
          faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : faqs;

  return (
    <div className="bg-white min-h-screen relative overflow-hidden">
      <SEO
        title="Help Center — Engaged Manila Support"
        description="Find answers to common questions about Engaged Manila. Get help with campaigns, payments, account settings, and influencer collaborations."
        url="/help-center"
      />
      <Header />

      {/* Unified Hero + Categories */}
      <div
        className="relative z-10 pt-24 pb-16 px-4 sm:px-6 lg:px-8"
        style={{
          background:
            'linear-gradient(180deg, #eff6ff 0%, #f5f3ff 35%, #fefce8 70%, #ffffff 100%)',
        }}
      >
        {/* Decorative blurred blobs */}
        <div className="absolute top-20 right-[5%] w-64 h-64 rounded-full bg-primary opacity-5 blur-3xl pointer-events-none" />
        <div className="absolute bottom-20 left-[5%] w-80 h-80 rounded-full bg-blue-400 opacity-5 blur-3xl pointer-events-none" />

        <div className="max-w-4xl mx-auto relative">
          {/* Title */}
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="text-base text-primary font-semibold tracking-wide uppercase">Support</h2>
            <p className="mt-2 text-4xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-5xl">
              Help Center
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto">
              Find answers to common questions and get the support you need to make the most of Engaged Manila.
            </p>
          </motion.div>

          {/* Search bar */}
          <motion.div
            className="mt-8 max-w-xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="relative">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search for help..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-14 pr-6 py-3.5 rounded-full border border-gray-200 bg-white shadow-sm text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              />
            </div>
          </motion.div>

          {/* Category cards — directly below search, no second title */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {helpCategories.map((category, index) => {
              const Icon = category.icon;
              return (
                <motion.div
                  key={category.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                >
                  <Link
                    to={category.link}
                    className="block bg-white rounded-2xl border border-gray-100 shadow-sm p-6 hover:shadow-lg transition-all duration-300"
                  >
                    <motion.div
                      whileHover={{ y: -4 }}
                      className="flex flex-col h-full"
                    >
                      <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl ${category.color} mb-4`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{category.title}</h3>
                      <p className="text-sm text-gray-500 leading-relaxed">{category.description}</p>
                    </motion.div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Popular Articles — quick links */}
      <div className="relative z-10 py-12 bg-white border-t border-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h3 className="text-lg font-semibold text-gray-900">Popular Articles</h3>
            <p className="mt-1 text-sm text-gray-500">Quick answers to the questions we hear most</p>
          </motion.div>
          <div className="flex flex-wrap justify-center gap-3">
            {popularArticles.map((article, index) => (
              <motion.a
                key={article.title}
                href="#faq"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-50 border border-gray-100 text-sm font-medium text-gray-700 hover:bg-primary/5 hover:border-primary/20 hover:text-primary transition-all duration-300"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
              >
                {article.title}
                <ArrowRight className="w-3.5 h-3.5" />
              </motion.a>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div id="faq" className="bg-gray-50 relative z-10">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-8 pt-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-base text-primary font-semibold tracking-wide uppercase">FAQ</h2>
            <p className="mt-2 text-4xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-5xl">
              Common Questions
            </p>
          </motion.div>
          {filteredFaqs.length > 0 ? (
            <FAQScrollAccordion items={filteredFaqs} title={null} className="pt-0 pb-16 md:pb-24" />
          ) : (
            <div className="text-center py-16">
              <p className="text-gray-500">No results found for "{searchQuery}". Try a different search.</p>
            </div>
          )}
        </div>
      </div>

      {/* CTA Section */}
      <motion.div
        className="bg-primary relative overflow-hidden z-10"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            className="absolute -right-20 -bottom-20 w-80 h-80 rounded-full bg-white opacity-10"
            animate={{ scale: [1, 1.2, 1], x: [0, -20, 0], y: [0, -20, 0] }}
            transition={{ duration: 15, repeat: Infinity, repeatType: 'reverse' }}
          />
          <motion.div
            className="absolute -left-10 top-10 w-40 h-40 rounded-full bg-blue-300 opacity-10"
            animate={{ scale: [1, 1.3, 1], x: [0, 20, 0], y: [0, 20, 0] }}
            transition={{ duration: 20, repeat: Infinity, repeatType: 'reverse' }}
          />
        </div>
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between relative z-10">
          <div className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            <div className="block">
              Still need help?
            </div>
            <div className="block text-blue-100">
              Our team is here for you.
            </div>
          </div>
          <div className="mt-8 flex lg:mt-0 lg:flex-shrink-0">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-primary bg-white hover:bg-blue-50 transition-colors duration-300 cursor-pointer shadow"
            >
              Contact Us
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </motion.div>

      <Footer />
    </div>
  );
};

export default HelpCenter;
